import { debounce } from "lodash";
import moment from "moment";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as CommitteeMeetingActions from "../../../actions/committeeMeeting";
import * as DiscussionActions from "../../../actions/discussion";
import * as NavigationActions from "../../../actions/navigation";
import * as ProjectActions from "../../../actions/project";
import * as TrialActions from "../../../actions/trial";
import { NotificationAlert } from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    TextAreaFormInput,
} from "../../../components/form";
import { SelectFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    ProjectDiscussionCreateBreadcrumbs,
    ProjectDiscussionDetailsBreadcrumbs,
    TrialProjectDiscussionCreateBreadcrumbs,
    TrialProjectDiscussionDetailsBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import {
    ProjectDiscussionSubMenu,
    TrialProjectDiscussionSubMenu
} from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ProjectLayout } from "../../index";

import "./DiscussionPage.scss";

interface DiscussionPageProps {
    form: Dtos.Discussion & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    projectId: number;
    discussion: Dtos.Discussion;
    project: Dtos.Project;
    lookups: Dtos.Lookup[];
    trial: Dtos.Trial;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    loadingProject: boolean;
    loadProjectSuccess: boolean;
    loadProjectFailure: boolean;
    loadProjectErrorCode: string;
    loadingDiscussion: boolean;
    loadDiscussionSuccess: boolean;
    loadDiscussionFailure: boolean;
    loadDiscussionErrorCode: string;
    savingDiscussion: boolean;
    saveDiscussionSuccess: boolean;
    saveDiscussionFailure: boolean;
    creatingDiscussion: boolean;
    createDiscussionSuccess: boolean;
    validatingDiscussion: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    committeeMeetings: Dtos.CommitteeMeeting[];
    loadingCommitteeMeetings: boolean;
    clearProject: ProjectActions.IProjectClearActionCreator;
    loadProject: ProjectActions.IProjectLoadByIdActionCreator;
    clearDiscussion: DiscussionActions.IDiscussionClearActionCreator;
    loadDiscussion: DiscussionActions.IDiscussionLoadByIdActionCreator;
    saveDiscussion: DiscussionActions.IDiscussionSaveActionCreator;
    createDiscussion: DiscussionActions.IDiscussionCreateActionCreator;
    validateDiscussion: DiscussionActions.IDiscussionValidateActionCreator;
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    loadCommitteeMeetings: CommitteeMeetingActions.ICommitteeMeetingLoadByCommitteeIdActionCreator;
    clearCommitteeMeetings: CommitteeMeetingActions.ICommitteeMeetingClearActionCreator;
}

const reduxFormName: string = "reduxForms.discussion";

const DiscussionPage: FC<DiscussionPageProps> = ({
    form,
    formState,
    formProperties,
    discussion,
    project,
    lookups,
    trial,
    loadingTrial,
    loadTrialFailure,
    loadTrialErrorCode,
    loadingProject,
    loadProjectFailure,
    loadProjectErrorCode,
    loadingDiscussion,
    loadDiscussionFailure,
    loadDiscussionErrorCode,
    savingDiscussion,
    saveDiscussionSuccess,
    saveDiscussionFailure,
    creatingDiscussion,
    createDiscussionSuccess,
    validatingDiscussion,
    validationFailures,
    permissions,
    committeeMeetings,
    loadingCommitteeMeetings,
    clearProject,
    loadProject,
    clearDiscussion,
    loadDiscussion,
    saveDiscussion,
    createDiscussion,
    validateDiscussion,
    clearTrial,
    loadTrial,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    loadCommitteeMeetings,
    clearCommitteeMeetings
}) => {
    //
    // -- Params --
    const { projectId } = useParams<{ projectId: string }>();
    const projectIdNumber = parseInt(projectId, 10);
    const { discussionId } = useParams<{ discussionId: string }>();
    const discussionIdNumber = parseInt(discussionId, 10);

    //
    // -- Hooks --
    const location = useLocation();
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [prevCommitteeId, setPrevCommitteeId] = useState<number | undefined>(undefined);
    const [updatedBreadcrumbs, setUpdatedBreadcrumbs] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.Discussion = {} as Dtos.Discussion;

        loadForm(reduxFormName, Object.assign({ ...discussion }, extraData));
    };


    // Load Project
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (baseRoute === "projects" && projectIdNumber && !project && !loadingProject){
            loadProject(projectIdNumber);
        }
    }, [
        location,
        projectIdNumber,
        project,
        loadingProject
    ]);

    // Load Trial
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (baseRoute === "trials" && projectIdNumber && !trial && !loadingTrial){
            loadTrial(projectIdNumber);
        }
    }, [
        location,
        projectIdNumber,
        trial,
        loadingTrial
    ]);

    // Load/Create Discussion
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (projectIdNumber && discussionIdNumber && !discussion && !loadingDiscussion) {
            if (baseRoute === "trials") {
                setDrawerItems(
                    TrialProjectDiscussionSubMenu(
                        projectIdNumber,
                        discussionIdNumber,
                        permissions
                    )
                );
                setBreadcrumbs(
                    TrialProjectDiscussionDetailsBreadcrumbs(
                        projectIdNumber,
                        discussionIdNumber
                    )
                );
            }
            else if (baseRoute === "projects") {
                setDrawerItems(
                    ProjectDiscussionSubMenu(
                        projectIdNumber,
                        discussionIdNumber,
                        permissions
                    )
                );
                setBreadcrumbs(
                    ProjectDiscussionDetailsBreadcrumbs(
                        projectIdNumber,
                        discussionIdNumber
                    )
                );
            }

            loadDiscussion(discussionIdNumber);
        } 
        else if (!discussionIdNumber && projectIdNumber && !creatingDiscussion && !createDiscussionSuccess) {
            if (baseRoute === "trials") {
                setBreadcrumbs(
                    TrialProjectDiscussionCreateBreadcrumbs(projectIdNumber)
                );
            } else if (baseRoute === "projects") {
                setBreadcrumbs(
                    ProjectDiscussionCreateBreadcrumbs(projectIdNumber)
                );
            }
            setDrawerItems([]);
            createDiscussion(projectIdNumber);
        } else if (discussion && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        location,
        projectIdNumber,
        discussionIdNumber,
        permissions,
        initalised,
        creatingDiscussion,
        createDiscussionSuccess,
        discussion,
        loadingDiscussion
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectFailure) {
            navigate(`/error/${loadProjectErrorCode}`);
        }
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadDiscussionFailure) {
            navigate(`/error/${loadDiscussionErrorCode}`);
        }
    }, [
        loadProjectFailure,
        loadTrialFailure,
        loadDiscussionFailure,
        loadProjectErrorCode,
        loadTrialErrorCode,
        loadDiscussionErrorCode
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (projectIdNumber && baseRoute === "trials" && trial?.displayName && trial?.projectProtocolID && !updatedBreadcrumbs) {
            if (discussionIdNumber) {
                setDrawerItems(
                    TrialProjectDiscussionSubMenu(
                        projectIdNumber,
                        discussionIdNumber,
                        permissions
                    )
                );
                setBreadcrumbs(
                    TrialProjectDiscussionDetailsBreadcrumbs(
                        projectIdNumber,
                        discussionIdNumber,
                        trial.displayName,
                        trial.projectProtocolID
                    )
                );
            } else {
                setBreadcrumbs(
                    TrialProjectDiscussionCreateBreadcrumbs(
                        projectIdNumber,
                        trial.displayName
                    )
                );
            }
            setUpdatedBreadcrumbs(true);
        } else if (projectIdNumber && baseRoute === "projects" && project?.projectShortTitle && !updatedBreadcrumbs) {
            if (discussionIdNumber) {
                setDrawerItems(
                    ProjectDiscussionSubMenu(
                        projectIdNumber,
                        discussionIdNumber,
                        permissions
                    )
                );
                setBreadcrumbs(
                    ProjectDiscussionDetailsBreadcrumbs(
                        projectIdNumber,
                        discussionIdNumber,
                        project.projectShortTitle
                    )
                );
            } else {
                setBreadcrumbs(
                    ProjectDiscussionCreateBreadcrumbs(
                        projectIdNumber,
                        project.projectShortTitle
                    )
                );
            }
            setUpdatedBreadcrumbs(true);
        }
    }, [
        projectIdNumber,
        discussionIdNumber,
        project,
        permissions,
        trial,
        location,
        updatedBreadcrumbs,
    ]);

    // Fetch committee meeting dropdown options
    useEffect(() => {
        if (form?.committeeId && form?.committeeId != prevCommitteeId) {
            loadCommitteeMeetings(form.committeeId);
            setPrevCommitteeId(form.committeeId);
        }
    }, [form, prevCommitteeId]);

    function generateOptionLabel(committeeMeeting: Dtos.CommitteeMeeting): string {
        const parts: string[] = [];

        if (committeeMeeting.meetingDate) {
            parts.push(
                moment(committeeMeeting.meetingDate, "YYYY-MM-DD HH:mm:ss.SSSSSSS Z").format("DD/MM/YYYY")
            );
        }

        if (committeeMeeting.location) parts.push(committeeMeeting.location);
        if (committeeMeeting.room) parts.push(committeeMeeting.room);
        if (committeeMeeting.time) parts.push(committeeMeeting.time);

        return parts.join(" - ");
    }

    // Generate committee meeting dropdown options
    const committeeMeetingOptions = useMemo(() => {
        let options = [];

        if (committeeMeetings) {
            committeeMeetings
                ?.sort((a, b) => a?.meetingDate.localeCompare(b?.meetingDate))
                .forEach((committeeMeeting) => {
                    options.push({
                        label: generateOptionLabel(committeeMeeting),
                        value: committeeMeeting.id,
                        disabled: false,
                    });
                });
        }

        return options;
    }, [committeeMeetings]);

    // Reload discussion after saving
    useEffect(() => {
        if (!savingDiscussion && saveDiscussionSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Discussion saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];
            if (saveAndReturn) {
                let url: string =
                    baseRoute === "trials"
                        ? `/trials/${projectIdNumber}/project/details`
                        : `/projects/${projectIdNumber}/details`;
                navigate(url);
            } else if (!projectIdNumber) {
                let url: string =
                    baseRoute === "trials"
                        ? `/trials/${trial.id}/project/discussions/${discussion.id}/details`
                        : `/projects/${project.id}/discussions/${discussion.id}/details`;
                navigate(url);
                clearProject();
            } else {
                if (baseRoute === "trials") {
                    loadProject(projectIdNumber);
                    loadTrial(projectIdNumber);
                    loadDiscussion(discussionIdNumber);
                } else {
                    loadProject(projectIdNumber);
                    loadDiscussion(discussionIdNumber);
                }
            }
        }
    }, [
        savingDiscussion,
        saveDiscussionSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingDiscussion && saveDiscussionFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingDiscussion, saveDiscussionSuccess]);

    // Cleanup effect to clear the discussion, project and trial on component unmount
    useEffect(() => {
        return () => {
            clearProject();
            clearTrial();
            clearDiscussion();
            clearCommitteeMeetings();
            resetForm(reduxFormName);
        };
    }, [
        clearProject,
        clearTrial,
        clearDiscussion,
        clearCommitteeMeetings,
        resetForm,
        reduxFormName,
    ]);

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];
        let url: string =
            baseRoute === "trials"
                ? `/trials/${trial.id}/project/discussions`
                : `/projects/${project.id}/discussions`;
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.Project) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.Discussion = {} as Dtos.Discussion;

            await saveDiscussion(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.Discussion = {} as Dtos.Discussion;

            if (form && hasBeenSubmitted && !validatingDiscussion) {
                await validateDiscussion(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const studyChairSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.studyChair 
                ? { label: form?.studyChair?.displayWithSalutation, value: form?.studyChairId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const coChairSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.coChair 
                ? { label: form?.coChair?.displayWithSalutation, value: form?.coChairId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const baseRoute = location?.pathname?.split("/")[1];
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionForId(
                projectIdNumber,
                permissions,
                Dtos.Permission.ProjectDiscussionUpdate,
                Dtos.Permission.ProjectDiscussionCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingDiscussion ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled) ||
            baseRoute === "trials";
        const conditionalDisabled: boolean =
            form.committeeId === 4 && form.outcomeId === 4 ? false : true;
        const conditionalDisabled2: boolean =
            form.committeeId !== 4 ||
            (form.committeeId === 4 && form.outcomeId === 4)
                ? false
                : true;

        if (conditionalDisabled) {
            changeForm(reduxFormName + ".studyChairId", null);
            changeForm(reduxFormName + ".coChairId", null);
            changeForm(reduxFormName + ".endorseDate", null);
            changeForm(reduxFormName + ".approvalId", null);
            changeForm(reduxFormName + ".approvalDate", null);
            changeForm(reduxFormName + ".fundingId", null);
            changeForm(reduxFormName + ".fundingDate", null);
            changeForm(reduxFormName + ".approvalComment", null);
        }
        if (conditionalDisabled2) {
            changeForm(reduxFormName + ".submitFundingId", null);
            changeForm(reduxFormName + ".submitFundingDate", null);
            changeForm(reduxFormName + ".submitFundingToId", null);
        }
        if (form.submitFundingId !== 2) {
            changeForm(reduxFormName + ".submitFundingDate", null);
            changeForm(reduxFormName + ".submitFundingToId", null);
        }
        if (!(form.approvalId !== 1)) {
            changeForm(reduxFormName + ".approvalDate", null);
        }
        if (!(form.fundingId !== 1)) {
            changeForm(reduxFormName + ".fundingDate", null);
        }

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            committeeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Discussion",
                                                                "CommitteeId",
                                                                lookups
                                                            ),
                                                            form?.committeeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                                changeAction: (
                                                    _model,
                                                    value,
                                                    _form,
                                                    _parentModel
                                                ) => {
                                                    changeForm(
                                                        reduxFormName +
                                                            ".committeeId",
                                                        value
                                                    );
                                                    if (
                                                        value != prevCommitteeId
                                                    ) {
                                                        changeForm(
                                                            reduxFormName +
                                                                ".committeeMeetingId",
                                                            null
                                                        );
                                                    }
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            committeeMeetingId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    loading:
                                                        loadingCommitteeMeetings,
                                                    options:
                                                        committeeMeetingOptions,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            outcomeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Discussion",
                                                                "outcomeId",
                                                                lookups
                                                            ),
                                                            form?.outcomeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            comment: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            chairGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Chairs</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            studyChairId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled || conditionalDisabled,
                                                inputProps: {
                                                    value: form?.studyChairId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.studyChair
                                                        ? [{ label: form.studyChair.displayWithSalutation, value: form.studyChair.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: studyChairSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            coChairId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled || conditionalDisabled,
                                                inputProps: {
                                                    value: form?.coChairId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.coChair
                                                        ? [{ label: form.coChair.displayWithSalutation, value: form.coChair.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: coChairSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            endorseDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    conditionalDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            fundingGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Approval & Funding</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            submitFundingId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    conditionalDisabled2,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Discussion",
                                                                "submitFundingId",
                                                                lookups
                                                            ),
                                                            form?.submitFundingId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-5",
                                                fieldClassName:
                                                    "col-sm-6 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-2">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            submitFundingDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    form?.submitFundingId !== 2,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-0 d-none",
                                                fieldClassName:
                                                    "col-sm-12 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-4">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            submitFundingToId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    form?.submitFundingId !== 2,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Discussion",
                                                                "submitFundingToId",
                                                                lookups
                                                            ),
                                                            form?.submitFundingToId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            approvalId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    conditionalDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Discussion",
                                                                "approvalId",
                                                                lookups
                                                            ),
                                                            form?.approvalId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-5",
                                                fieldClassName:
                                                    "col-sm-6 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            approvalDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !(form.approvalId === 2),
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            fundingId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    conditionalDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "Discussion",
                                                                "fundingId",
                                                                lookups
                                                            ),
                                                            form?.fundingId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-5",
                                                fieldClassName:
                                                    "col-sm-6 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            fundingDate: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType:
                                                    DatePickerFormInputFabric,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !(form.fundingId === 2),
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "discussion",
                                        {
                                            approvalComment: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    conditionalDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-1",
                                                fieldClassName:
                                                    "col-sm-11 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "discussion",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(
                                reduxFormName + ".saveAndReturn",
                                false
                            );
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(
                                reduxFormName + ".saveAndReturn",
                                true
                            );
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button
                        onClick={() => onCancel()}
                        className="btn btn-primary mr-2"
                    >
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        projectIdNumber,
        permissions,
        savingDiscussion,
        formState,
        form,
        lookups,
        navigate,
        PAGE_SIZE_OPTIONS,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        validationFailures,
        formProperties,
        changeForm,
        loadingCommitteeMeetings,
        committeeMeetingOptions,
        studyChairSearchLoader,
        coChairSearchLoader
    ]);

    return (
        <ProjectLayout
            loading={
                loadingProject ||
                loadingDiscussion ||
                savingDiscussion ||
                creatingDiscussion ||
                loadingTrial
            }
            permission={Dtos.Permission.ProjectDiscussionView}
        >
            {renderContent}
        </ProjectLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<DiscussionPageProps> => {
    let project: Dtos.Project | undefined = !(
        state.projects.formData instanceof Array
    )
        ? state.projects.formData
        : undefined;
    let discussion: Dtos.Discussion | undefined = !(
        state.discussions.formData instanceof Array
    )
        ? state.discussions.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;
    let committeeMeetings: Dtos.CommitteeMeeting[] | undefined =
        state.committeeMeeting.formData instanceof Array
            ? state.committeeMeeting.formData
            : undefined;

    return {
        discussion: discussion!,
        project: project!,
        trial: trial!,
        committeeMeetings: committeeMeetings!,
        loadingTrial:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Success,
        loadTrialFailure:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        loadingProject:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Pending,
        loadProjectSuccess:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Success,
        loadProjectFailure:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Failure,
        loadProjectErrorCode: state.projects.loadState.errorCode,
        loadingDiscussion:
            state.discussions.loadState &&
            state.discussions.loadState.status == RequestState.Pending,
        loadDiscussionSuccess:
            state.discussions.loadState &&
            state.discussions.loadState.status == RequestState.Success,
        loadDiscussionFailure:
            state.discussions.loadState &&
            state.discussions.loadState.status == RequestState.Failure,
        loadDiscussionErrorCode: state.discussions.loadState.errorCode,
        savingDiscussion:
            state.discussions.saveState &&
            state.discussions.saveState.status == RequestState.Pending,
        saveDiscussionSuccess:
            state.discussions.saveState &&
            state.discussions.saveState.status == RequestState.Success,
        saveDiscussionFailure:
            state.discussions.saveState &&
            state.discussions.saveState.status == RequestState.Failure,
        creatingDiscussion:
            state.discussions.createState &&
            state.discussions.createState.status == RequestState.Pending,
        createDiscussionSuccess:
            state.discussions.createState &&
            state.discussions.createState.status == RequestState.Success,
        validatingDiscussion:
            state.discussions.validateState &&
            state.discussions.validateState.status == RequestState.Pending,
        loadingCommitteeMeetings:
            state.committeeMeeting.loadState &&
            state.committeeMeeting.loadState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.discussions.lookups,
        form: state.reduxForms.discussion,
        formState: state.discussions.formState,
        formProperties: state.discussions.formProperties,
        validationFailures: project
            ? state.discussions.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<DiscussionPageProps> => {
    return {
        loadProject: bindActionCreators(
            ProjectActions.LoadProjectById,
            dispatch
        ),
        clearProject: bindActionCreators(ProjectActions.Clear, dispatch),
        loadDiscussion: bindActionCreators(
            DiscussionActions.LoadDiscussionById,
            dispatch
        ),
        clearDiscussion: bindActionCreators(DiscussionActions.Clear, dispatch),
        saveDiscussion: bindActionCreators(
            DiscussionActions.SaveDiscussion,
            dispatch
        ),
        createDiscussion: bindActionCreators(
            DiscussionActions.CreateDiscussion,
            dispatch
        ),
        validateDiscussion: bindActionCreators(
            DiscussionActions.ValidateDiscussion,
            dispatch
        ),
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch),
        loadCommitteeMeetings: bindActionCreators(
            CommitteeMeetingActions.LoadCommitteeMeetingByCommitteeId,
            dispatch
        ),
        clearCommitteeMeetings: bindActionCreators(
            CommitteeMeetingActions.Clear,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(DiscussionPage);
